import useSessionStore from "@features/session/hooks/useSessionStore";
import { isItemAnswered } from "@memorang/helpers/item";
import type {
	BaseItemSchema,
	ItemResponse,
	OriginalItemTypes,
} from "@memorang/types";
import { useCallback, useMemo } from "react";
import ItemWrapper from "../../ItemWrapper";
import NoteCompletion from "./variants/NoteCompletion";

type Props = {
	item: BaseItemSchema;
	response: Extract<ItemResponse, { type: "form" }>;
	originalItemType: OriginalItemTypes;
	toggleShowReadMore?: () => void;
};

const WrittenFormContainer = ({
	item,
	response,
	originalItemType,
	toggleShowReadMore,
}: Props) => {
	const itemId = item.id!;
	const setTextResponse = useSessionStore((state) => state.setTextResponse);
	const itemStates = useSessionStore((state) => state.itemStates);
	const isSummativeTest = useSessionStore((state) => state.isSummativeTest);
	const inReviewMode = useSessionStore((state) => state.inReviewMode);

	const itemState = itemStates?.get(itemId);
	const isAnswered =
		Boolean(!isSummativeTest && itemState?.answered) ||
		isItemAnswered({ item, isSummativeTest, inReviewMode });

	const answers = useSessionStore((state) => state.answers);

	const textValue =
		itemState?.selectedChoiceIds?.[0] ??
		answers?.get(itemId)?.selectedChoiceIds?.[0] ??
		item.answer?.selectedChoiceIds?.[0];

	const correct = useMemo(() => {
		if (!(textValue && response.fields?.length)) {
			return undefined;
		}
		return { [response.fields[0].id]: { response: textValue } };
	}, [textValue, response.fields]);

	const handleUpdateFormStates = useCallback(
		(states: Record<string, string>) => {
			const value = Object.values(states)[0] ?? "";
			setTextResponse(itemId, value);
		},
		[itemId, setTextResponse],
	);

	return (
		<ItemWrapper
			item={item}
			response={response}
			originalItemType={originalItemType}
			toggleShowReadMore={toggleShowReadMore}
		>
			<NoteCompletion
				key={itemId}
				itemResponse={response}
				updateFormStates={handleUpdateFormStates}
				disabled={isAnswered}
				correct={correct}
			/>
		</ItemWrapper>
	);
};

export default WrittenFormContainer;
